import styled from "styled-components";
import { useEffect, useState, useMemo, useRef } from "react";
import Hero from "src/components/Hero/Hero";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import TitleSection from "src/components/TitleSection/TitleSection";
import EventFinished from "src/components/EventFinished";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";
import DynamicGoogleFormSubmitter from "src/components/Form/DynamicGoogleFormSubmitter";
import Button from "src/components/Button/Button";
import SpinWheelModal from "src/components/SpinWheel/SpinWheelModal";
import wheelConfigDefault from "src/config/wheelConfigDefault";

const ScavengerHunt2025 = () => {
  const { t, i18n } = useTranslation();
  const [showWheel, setShowWheel] = useState(false);
  const [spinPrize, setSpinPrize] = useState("");
  const formRef = useRef(null);
  const customButtonRef = useRef(null);

  // Move the custom button inside the form container so it gets covered by success overlay
  useEffect(() => {
    const formContainer = formRef.current?.querySelector('.form-container');
    const customButton = customButtonRef.current;

    if (formContainer && customButton && !formContainer.contains(customButton)) {
      formContainer.appendChild(customButton);
    }
  }, []);

  // Build wheel config from default + i18n text strings
  const wheelData = useMemo(() => {
    const wheelTextSegments = t('scavengerHunt2025.wheel.segments', { returnObjects: true });

    // Merge structural data with localized text
    const segmentValuesArray = [
      { probability: 0, type: "string", win: true, userData: { prize: 0 }, ...wheelTextSegments[4] },
      { probability: 75, type: "string", win: true, userData: { prize: 100 }, ...wheelTextSegments[0] },
      { probability: 0, type: "string", win: true, userData: { prize: 0 }, ...wheelTextSegments[4] },
      { probability: 5, type: "string", win: true, userData: { prize: 50 }, ...wheelTextSegments[1] },
      { probability: 10, type: "string", win: true, userData: { prize: 20 }, ...wheelTextSegments[2] },
      { probability: 10, type: "string", win: true, userData: { prize: 10 }, ...wheelTextSegments[3] }
    ];

    // Custom color array to match number of segments
    const colorArray = [
      "#2d2d2d",  // 0
      "#fcb441",  // 100
      "#2d2d2d",  // 0
      "#6bb3a1",  // Light Salmon
      "#da6a3e",  // Mint
      "#57a96f"   // Bright Yellow
    ];

    return {
      ...wheelConfigDefault,
      segmentValuesArray,
      colorArray,
      centerCircleFillColor: "#000",
      centerCircleStrokeColor: "#F39C12",
      introText: ''
    };
  }, [t]);

  // Custom submit handler that shows wheel first
  const handleCustomSubmit = () => {
    // Validate form first
    const form = formRef.current?.querySelector('form');
    if (form && !form.checkValidity()) {
      // Trigger HTML5 validation
      form.reportValidity();
      return;
    }

    // Show wheel modal
    setShowWheel(true);
  };

  // Handle prize won - set prize and auto-submit
  const handlePrizeWon = (prize) => {
    setSpinPrize(String(prize));
    // Don't close modal - keep it open
    // setShowWheel(false);

    // Auto-submit the form after a brief delay
    setTimeout(() => {
      const form = formRef.current?.querySelector('form');
      if (form) {
        console.log('Prize won, submitting form with prize:', prize);
        form.requestSubmit();
      }
    }, 500);
  };

  return (
    <UltimateDadDiv>
      <EventFinished
        scheduleKeyName="scavengerHunt2025"
        eventType="promotion"
      />
      <Hero
        bgImage="promotions/gaming-machines/win-a-million-2025/WAM-Scavenger-hunt-header-hero-image-2400x800px.jpg"
        image={`promotions/gaming-machines/win-a-million-2025/WAM-Scavenger-hint-logo-2400x2400px-${i18n.language.toUpperCase()}.png`}
        imageAlt="Win a Million Scavenger Hunt Logo"
        fluidImage={false}
      />

      <MainContentDiv>
        <container-div>
          <SpinWinReceiveTitle>
            {t("scavengerHunt2025.content.subTitle")}
          </SpinWinReceiveTitle>
          <DescriptionText>
            <ReactMarkdown>
              {t("scavengerHunt2025.content.description")}
            </ReactMarkdown>
          </DescriptionText>

          <ClaimYourEntryTitle>
            {t("scavengerHunt2025.content.claimYourEntry")}
          </ClaimYourEntryTitle>
        </container-div>
        <Row>
          <Col xs={12} className="mx-auto">
            <FormWrapper ref={formRef}>
              <DynamicGoogleFormSubmitter
                formId="AKfycbzieyw0DzbQNQ_RaU7IOpcNCJ2Zi2SN6Vi_VusW0Bw8_3lB95ci0KKEAR9OjWNxaOEsNg" // v4.5
                sheetName="Fortune 0"
                // Sheet names
                // Fortune 0
                // Dragon 1
                // Wheel 2
                // Mummy 3
                // Gift 4
                // Elevator 5
                formTextKey="scavengerHunt2025"
                titles={false}
                values={{ Prize: spinPrize }}
                formFields={[
                  { id: "FirstName", label: "firstName", type: "text", required: true },
                  { id: "LastName", label: "lastName", type: "text", required: true },
                  { id: "Email", label: "email", type: "email", required: true },
                  { id: "PhoneNumber", label: "phoneNumber", type: "text", required: true },
                  { id: "PlayerID", label: "pgPlayerId", type: "text", required: true, placeholder: "pgPlayerIdPlaceholder" },
                ]}
              />
              {/* Custom Submit Button that will be moved inside form container via JS */}
              <CustomSubmitButtonContainer ref={customButtonRef}>
                <Button
                  onClick={handleCustomSubmit}
                  bgColor="var(--red)"
                  color="var(--white)"
                  hoverColor="var(--black)"
                  hoverBgColor="var(--white)"
                  size="large"
                  width="wide"
                  label={t("scavengerHunt2025.content.spinNow")}
                />
              </CustomSubmitButtonContainer>
            </FormWrapper>
          </Col>
        </Row>
        <Row className="small pt-4 pb-4">
          <Col xs={12} md={12} lg={8} className="mx-auto">
            <FormSmallPrint>
              <ReactMarkdown>
                {t("scavengerHunt2025.content.formSmallPrint")}
              </ReactMarkdown>
            </FormSmallPrint>
          </Col>
        </Row>

        <BonusPrizeSection>
          <h3>{t("scavengerHunt2025.content.extraPrize")}</h3>
          <div className="prize-text">
            <p>{t("scavengerHunt2025.content.extraPrizeDetails")}</p>
          </div>
        </BonusPrizeSection>

        {/* <Row className="mt-3 mb-4">
          <Col xs={12} className="mx-auto text-center">
            <Button
              label={i18n.language?.startsWith("fr") ? "Tournez pour gagner" : "Spin to Win"}
              onClick={() => setShowWheel(true)}
              size="large"
              width="wide"
              specialHover
            />
          </Col>
        </Row> */}

        <container-div>
          <Row className="g-4 mb-4 mt-4">
            <Col xs={12} md={12} lg={10} className="mx-auto">
              <ContentCard
                bgImage={useResponsiveImageUrl(
                  "promotions/gaming-machines/win-a-million-2025/sparkle-box.png"
                )}
              >
                <CardHeader>
                  {t("scavengerHunt2025.content.aboutWinAMillion")}
                </CardHeader>
                <CardContent>
                  <ReactMarkdown>
                    {t("scavengerHunt2025.content.aboutWinAMillionText")}
                  </ReactMarkdown>
                  <StyledButtonWrapper>
                    <Button
                      bgColor="#c9a961"
                      color="var(--black)"
                      hoverBgColor="var(--white)"
                      hoverColor="var(--black)"
                      label={t("button.learn-more")}
                      href={t("routes.winAMillion2025")}
                      width="wide"
                      size="large"
                    />
                  </StyledButtonWrapper>
                </CardContent>
              </ContentCard>
            </Col>
          </Row>
        </container-div>
      </MainContentDiv>
      <SpinWheelModal
        show={showWheel}
        onHide={() => setShowWheel(false)}
        onPrize={handlePrizeWon}
        data={wheelData}
        subscribeOnResult={false}
        // autoSpin={false}
      />
    </UltimateDadDiv>
  );
};

const UltimateDadDiv = styled.div`
  --topspacing: 110px;
  --gradientheight: 40px;

  width: 100%;
  height: auto;
  background-color: var(--lightblack);
  color: var(--white);
  text-align: center;
  overflow-x: hidden;
  padding-bottom: 40px;

  .hero_container {
    padding-top: 0;
  }

  .hero_text_container {
    display: none;
    padding: 0;
  }

  .hero_image_container {
    padding: 20px;
    min-height: 600px;
    @media (max-width: 768px) {
      min-height: 300px;
    }
  }

  .title-section {
    h2 {
      text-transform: uppercase;
      color: #f0cb43;
    }
    p {
      max-width: unset;
    }
  }

  .small {
    text-align: center;
  }

  strong {
    color: #f0cb43;
  }

  p,
  li {
    strong {
      color: #f0cb43;
    }
  }

  .scavenger-hunt-content {
    padding: 3rem 1rem;

    .title-section {
      margin-bottom: 2rem;
    }

    @media (max-width: 768px) {
      padding: 2rem 1rem;
    }
  }
`;

const BonusPrizeSection = styled.div`
  background-color: var(--black);
  padding: 2rem 1rem;
  text-align: center;

  h3 {
    color: #f0cb43;
    font-size: 2rem;
    font-weight: bold;
    margin-bottom: 1rem;
    text-transform: uppercase;
  }

  p {
    font-size: 1.2rem;
    color: white;
  }

  @media (max-width: 768px) {
    h3 {
      font-size: 1.5rem;
    }
    p {
      font-size: 1rem;
    }
  }
`;

const SpinWinReceiveTitle = styled.h2`
  color: #f0cb43;
  font-size: 2.5rem;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 2rem;
  letter-spacing: 0.1em;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const DescriptionText = styled.div`
  color: var(--white);
  font-size: 1.1rem;
  line-height: 1.6;
  text-align: center;
  max-width: 900px;
  margin: 0 auto 3rem;

  p {
    margin-bottom: 1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    color: #f0cb43;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const ClaimYourEntryTitle = styled.h3`
  color: #f0cb43;
  font-size: 2rem;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const FormSmallPrint = styled.div`
  color: var(--white);
  font-size: 0.9rem;
  line-height: 1.5;
  text-align: center;

  p {
    margin-bottom: 0.75rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    color: #f0cb43;
  }

  a {
    color: #f0cb43;
    text-decoration: underline;

    &:hover {
      color: var(--white);
    }
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const MainContentDiv = styled.div`
  padding-bottom: 1rem;
  background-color: var(--lightblack);

  @media (min-width: 768px) {
    padding: 0 1rem 1rem;
  }

  @media (min-width: 992px) {
    padding: 0 0 3rem;
  }

  height: auto;
  width: 100%;

  container-div {
    max-width: 1200px;
  }

  .row {
    display: flex;
    justify-content: space-between;
    max-width: 1200px;
    margin: 0 auto;
    position: relative;
  }
`;

const ContentCard = styled.div`
  background: url(${(props) => props.bgImage}) no-repeat;
  background-size: cover;
  background-position: 70% center;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CardHeader = styled.div`
  background: linear-gradient(135deg, #d4af37 0%, #f4e4a6 50%, #d4af37 100%);
  color: var(--black);
  padding: 0.2rem 1.5rem;
  font-size: 1.5rem;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.75rem 1rem;
  }
`;

const CardContent = styled.div`
  padding: 1.5rem;
  color: var(--white);
  flex: 1;
  text-align: center;

  p {
    margin-bottom: 0.75rem;
    line-height: 1.6;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    color: #f0cb43;
    font-weight: 600;
  }

  ul,
  ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
    text-align: left;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }

  a {
    display: inline-block;
    margin-top: 1rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 0.95rem;
  }
`;

const FormWrapper = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;

  /* Hide the original form submit button - more specific selector */
  && .form-container button[type="submit"],
  && .form-container a.btn[type="submit"] {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
    position: absolute !important;
    left: -9999px !important;
  }

  /* Style the form container */
  .form-container,
  form {
    width: 100%;
    background: transparent;
    border: none;
    max-width: unset;
    gap: 0;
    padding-top: 0;
    margin: 0;

    @media (max-width: 768px) {
      max-width: 100%;
    }
  }

  /* Make form fields display as table-like rows */
  .form-group,
  .mb-3,
  form > div {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    margin-bottom: 0;

    &:first-of-type label {
      border-top: 1px solid #000;
    }

    &:first-of-type .form-control {
      border-top: 1px solid #000;
    }
  }

  /* Style all labels - gray boxes on the left */
  .form-label {
    display: flex;
    align-items: center;
    background-color: #bebebe;
    border-left: 1px solid #000;
    border-right: 1px solid #000;
    border-bottom: 1px solid #000;
    padding: 0.75rem 1rem;
    margin: 0;
    color: #2c2c2c;
    font-size: 1rem;
    min-height: 56px;
    width: 280px;
    flex-shrink: 0;
    text-align: left;
    white-space: normal;

    @media (max-width: 768px) {
      width: 160px;
      font-size: 0.9rem;
      padding: 0.5rem 0.75rem;
      min-height: 48px;
    }
  }

  /* Style all input fields - lighter gray boxes on the right */
  .form-control {
    flex: 1;
    flex-grow: 1;
    background-color: #dedede;
    border-right: 1px solid #000;
    border-bottom: 1px solid #000;
    border-left: none;
    border-top: none;
    padding: 0.75rem 1rem;
    margin: 0;
    color: #2c2c2c;
    font-size: 1rem;
    min-height: 56px;
    min-width: 20ch;
    border-radius: 0;
    overflow-x: auto;

    &:focus {
      outline: none;
      box-shadow: none;
      background-color: #e8e8e8;
    }

    @media (max-width: 768px) {
      font-size: 0.9rem;
      padding: 0.5rem 0.75rem;
      min-height: 48px;
      min-width: 18ch;
    }
  }

  /* Style the submit button */
  .form-container button[type="submit"],
  .form-container a.btn {
    background: linear-gradient(
      135deg,
      #c9a961 0%,
      #f4e4a6 50%,
      #c9a961 100%
    ) !important;
    color: #000 !important;
    border: none !important;
    padding: 1rem 3.5rem !important;
    font-size: 1.15rem !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.05em !important;
    border-radius: 10px !important;
    cursor: pointer !important;
    margin-top: 2rem !important;
    max-width: fit-content;
    transition: all 0.3s ease !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;

    &:hover {
      transform: translateY(-2px) !important;
      box-shadow: 0 6px 16px rgba(201, 169, 97, 0.5) !important;
      opacity: 0.95 !important;
    }

    &:active {
      transform: translateY(0) !important;
    }

    &:disabled {
      opacity: 0.6 !important;
      cursor: not-allowed !important;
    }

    @media (max-width: 768px) {
      width: 100% !important;
      max-width: 350px !important;
      padding: 1rem 2rem !important;
      font-size: 1.05rem !important;
      min-width: unset !important;
      margin-top: 1.5rem !important;
    }
  }
`;

const CustomSubmitButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;

  @media (max-width: 768px) {
    margin-top: 1.5rem;
  }
`;

const StyledButtonWrapper = styled.div`
  a {
    background: linear-gradient(
      135deg,
      #c9a961 0%,
      #f4e4a6 50%,
      #c9a961 100%
    ) !important;
    padding: 1rem 3.5rem !important;
    font-size: 1.15rem !important;
    border-radius: 10px !important;
    min-width: 250px !important;

    &:hover {
      transform: translateY(-2px) !important;
      box-shadow: 0 6px 16px rgba(201, 169, 97, 0.5) !important;
      background: linear-gradient(
        135deg,
        #c9a961 0%,
        #f4e4a6 50%,
        #c9a961 100%
      ) !important;
    }

    @media (max-width: 768px) {
      max-width: 350px !important;
      padding: 1rem 2rem !important;
      font-size: 1.05rem !important;
      min-width: unset !important;
    }
  }
`;

export default ScavengerHunt2025;
